// @flow
import { get } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';

import {
  getPriceDetailFragment,
  transportDetailsFragment,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { PROPOSAL_GET_ADDITIONAL_TRANSPORT } from 'Utils/request/requestDictionary';
import createAction from 'Store/utils/createAction';
import {
  getAdditionalTransports,
  getAdditionalTransportsFilters,
} from 'Store/booking/booking.selector';

const CLEAR_ADDITIONAL_TRANSPORTS = 'CLEAR_ADDITIONAL_TRANSPORTS';
const FETCH_ADDITIONAL_TRANSPORTS = 'FETCH_ADDITIONAL_TRANSPORTS';
const CREATE_ADDITIONAL_TRANSPORTS_FILTERS = 'CREATE_ADDITIONAL_TRANSPORTS_FILTERS';
const SET_FILTERED_ADDITIONAL_TRANSPORTS = 'SET_FILTERED_ADDITIONAL_TRANSPORTS';
const TOGGLE_FILTER = 'TOGGLE_FILTER';
const DISABLE_FILTERS = 'DISABLE_FILTERS';
const UNSELECT_ADDITIONAL_TRANSPORTS_FILTER_CATEGORY =
  'UNSELECT_ADDITIONAL_TRANSPORTS_FILTER_CATEGORY';
const UPDATE_FILTERED_RESULTS = 'UPDATE_FILTERED_RESULTS';
const SORT_ADDITIONAL_TRANSPORTS = 'SORT_ADDITIONAL_TRANSPORTS';

function _createAdditionalTransportsFilters(i18n: *, getState: GetReduxState) {
  return createAction(CREATE_ADDITIONAL_TRANSPORTS_FILTERS, {
    additionalTransports: getAdditionalTransports(getState()),
    i18n,
  });
}

function _toggleFilter(filter: Filter, getState: GetReduxState) {
  return createAction(TOGGLE_FILTER, {
    additionalTransports: getAdditionalTransports(getState()),
    filter,
  });
}

function _disableFilters(filters: Array<Filter>, getState: GetReduxState) {
  return createAction(DISABLE_FILTERS, {
    additionalTransports: getAdditionalTransports(getState()),
    filters,
  });
}

function _unselectFiltersCategory(category: string, getState: GetReduxState) {
  return createAction(UNSELECT_ADDITIONAL_TRANSPORTS_FILTER_CATEGORY, {
    additionalTransports: getAdditionalTransports(getState()),
    category,
  });
}

function _updateFilteredTransports(getState: GetReduxState) {
  return createAction(UPDATE_FILTERED_RESULTS, {
    additionalTransports: getAdditionalTransports(getState()),
    filters: getAdditionalTransportsFilters(getState()),
  });
}

function _runAdditionalTransportsQuery(proposalId: string, appContext: AppContext): Promise<*> {
  const query = `
    {
      additionalTransports(proposalId : "${proposalId}") {
        id
        totalDifferentialPrice {...priceDetails}
        inbound { ...transportsDetails }
        outbound { ...transportsDetails }
        filterValues {
          cabinClasses
          companies { id label logo }
          layovers
          inboundDepartureTime { hours minutes }
          inboundArrivalTime { hours minutes }
          outboundDepartureTime { hours minutes }
          outboundArrivalTime { hours minutes }
        }
      }

      config {
        filters {
          cabinClass {
            economy
            business
          }
          layovers {
            direct
            stop {
              label_1
              label_n
            }
          }
          departureArrival {
            timeRanges {
              label
              icon
              start { hours minutes }
              end { hours minutes }
            }
          }
        }
      }
    }
    ${transportDetailsFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
  `;

  return graphQLClient.runNoCacheQuery(query, appContext, {
    requestName: PROPOSAL_GET_ADDITIONAL_TRANSPORT,
  });
}

function updateAdditionalTransportsSort(sorter: Sorter) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const additionalTransports = getAdditionalTransports(getState());
    const filters = getAdditionalTransportsFilters(getState());
    return dispatch(
      createAction(SORT_ADDITIONAL_TRANSPORTS, { additionalTransports, filters, sorter }),
    );
  };
}

function clearAdditionalTransports() {
  return createAction(CLEAR_ADDITIONAL_TRANSPORTS);
}

function fetchAdditionalTransports(proposalId: string, appContext: AppContext) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(createAction(FETCH_ADDITIONAL_TRANSPORTS));

    return _runAdditionalTransportsQuery(proposalId, appContext).then(
      ({ additionalTransports, config: i18n }) => {
        dispatch(createAction(resolve(FETCH_ADDITIONAL_TRANSPORTS), { additionalTransports }));

        dispatch(_createAdditionalTransportsFilters(i18n, getState));
        dispatch(_updateFilteredTransports(getState));
      },
      error => {
        dispatch(createAction(reject(FETCH_ADDITIONAL_TRANSPORTS), error));
      },
    );
  };
}

function toggleAdditionalTransportFilter(filter: Filter) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_toggleFilter(filter, getState));
    dispatch(_updateFilteredTransports(getState));
  };
}

function disableAdditionalTransportFilters(filters: Array<Filter>) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_disableFilters(filters, getState));
    dispatch(_updateFilteredTransports(getState));
  };
}

function unselectAdditionalTransportsFilterCategory(category: string) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_unselectFiltersCategory(category, getState));
    dispatch(_updateFilteredTransports(getState));
  };
}

export {
  CLEAR_ADDITIONAL_TRANSPORTS,
  CREATE_ADDITIONAL_TRANSPORTS_FILTERS,
  FETCH_ADDITIONAL_TRANSPORTS,
  SET_FILTERED_ADDITIONAL_TRANSPORTS,
  TOGGLE_FILTER,
  DISABLE_FILTERS,
  UNSELECT_ADDITIONAL_TRANSPORTS_FILTER_CATEGORY,
  UPDATE_FILTERED_RESULTS,
  SORT_ADDITIONAL_TRANSPORTS,
  clearAdditionalTransports,
  fetchAdditionalTransports,
  disableAdditionalTransportFilters,
  toggleAdditionalTransportFilter,
  unselectAdditionalTransportsFilterCategory,
  updateAdditionalTransportsSort,
};
